/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';
import { element } from '@instructure/ui-prop-types';
import { ThemeablePropTypes } from '@instructure/emotion';
import { PositionPropTypes } from '@instructure/ui-position';
const propTypes = {
  isShowingContent: PropTypes.bool,
  defaultIsShowingContent: PropTypes.bool,
  on: PropTypes.oneOfType([PropTypes.oneOf(['click', 'hover', 'focus']), PropTypes.arrayOf(PropTypes.oneOf(['click', 'hover', 'focus']))]),
  withArrow: PropTypes.bool,
  color: PropTypes.oneOf(['primary', 'primary-inverse']),
  placement: PositionPropTypes.placement,
  shadow: ThemeablePropTypes.shadow,
  stacking: ThemeablePropTypes.stacking,
  contentRef: PropTypes.func,
  defaultFocusElement: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  screenReaderLabel: PropTypes.string,
  offsetX: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  offsetY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  constrain: PositionPropTypes.constrain,
  positionTarget: PropTypes.oneOfType([element, PropTypes.func]),
  positionContainerDisplay: PropTypes.oneOf(['inline-block', 'block']),
  mountNode: PositionPropTypes.mountNode,
  insertAt: PropTypes.oneOf(['bottom', 'top']),
  liveRegion: PropTypes.oneOfType([element, PropTypes.arrayOf(element), PropTypes.func]),
  id: PropTypes.string,
  shouldAlignArrow: PropTypes.bool,
  shouldTrackPosition: PropTypes.bool,
  shouldRenderOffscreen: PropTypes.bool,
  shouldContainFocus: PropTypes.bool,
  shouldReturnFocus: PropTypes.bool,
  shouldCloseOnDocumentClick: PropTypes.bool,
  shouldCloseOnEscape: PropTypes.bool,
  shouldFocusContentOnTriggerBlur: PropTypes.bool,
  onShowContent: PropTypes.func,
  onHideContent: PropTypes.func,
  onPositioned: PropTypes.func,
  onPositionChanged: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseOut: PropTypes.func,
  renderTrigger: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  elementRef: PropTypes.func,
  borderWidth: ThemeablePropTypes.borderWidth,
  shouldSetAriaExpanded: PropTypes.bool
};
const allowedProps = ['isShowingContent', 'defaultIsShowingContent', 'on', 'withArrow', 'color', 'placement', 'shadow', 'stacking', 'contentRef', 'defaultFocusElement', 'screenReaderLabel', 'offsetX', 'offsetY', 'constrain', 'positionTarget', 'positionContainerDisplay', 'mountNode', 'insertAt', 'liveRegion', 'id', 'shouldAlignArrow', 'shouldTrackPosition', 'shouldRenderOffscreen', 'shouldContainFocus', 'shouldReturnFocus', 'shouldCloseOnDocumentClick', 'shouldCloseOnEscape', 'shouldFocusContentOnTriggerBlur', 'onShowContent', 'onHideContent', 'onPositioned', 'onPositionChanged', 'onClick', 'onFocus', 'onBlur', 'onKeyDown', 'onKeyUp', 'onMouseOver', 'onMouseOut', 'renderTrigger', 'children', 'elementRef', 'borderWidth', 'shouldSetAriaExpanded'];
export { propTypes, allowedProps };